import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { OrganisationTypeConstants, ResourcePermissionConstants, UserType } from "../../../constants";
import { getProjectPhaseCounts } from "../../../constants/project-phase";
import { TaskConstants } from "../../../constants/workflow/TaskConstants";
import { TabsList, UpcomingMilestonePropsWithKey } from "../../../models";
import { logError } from "../../../service/error";
import {
  getAggregateWorkflowTasks,
  getProjectAggregate,
  GetTaskBoardResponse,
  searchWorkflowTasks,
  SearchWorkflowTasksResponse,
} from "../../../service/query";
import { Status } from "../../../service/Shared";
import { useTaskPanelWorkflow } from "../../../templates/WorkflowTaskPanel";
import { useAuth } from "../../../useAuth";
import { getTaskParentObjectNavigation } from "../../../utils";
import { getNewProjectRoute, getProjectRoadmapRoute, getProjectTrackerRoute } from "../../../utils/routes";
import { ChartData, OverdueTaskProps } from "../../../widget";

interface UseProjectsReturnData {
  tabsList: TabsList;
  /** Seed cards are rounded; Hub cards keep square corners. */
  kpiCorners: "rounded" | "default";
  canCreateProject: boolean;
  onNewProjectClick: () => void;
  phaseChartData: ChartData;
  isLoading: boolean;
  currentUserType: UserType;
  upcomingMilestones: UpcomingMilestonePropsWithKey[];
  isLoadingMilestones: boolean;
  hasMilestonesError: boolean;
  overdueTasks: OverdueTaskProps;
  isOverdueTasksPanelOpen: boolean;
  setIsOverdueTasksPanelOpen: (isOpen: boolean) => void;
  isUpcomingMilestonesPanelOpen: boolean;
  setIsUpcomingMilestonesPanelOpen: (isOpen: boolean) => void;
  workflow: GetTaskBoardResponse["results"][number] | undefined;
  selectedPanelTask: SearchWorkflowTasksResponse["results"][0] | undefined;
  showWorkflowTaskPanel: boolean;
  handleCloseWorkflowTaskPanel: () => void;
  handleRefresh: () => void;
  refreshTasks: boolean;
  setSelectedPanelTask: (task: SearchWorkflowTasksResponse["results"][0] | undefined) => void;
  setShowWorkflowTaskPanel: (show: boolean) => void;
}

const getProjectAggregateOrganisationFilter = (
  currentUserType: UserType,
  currentDeveloperUuid: string | null | undefined,
  currentDevelopmentManagerUuid: string | null | undefined,
  currentAssetManagerUuid: string | null | undefined
): { filterBy: string; filterValue: string } => {
  if (currentUserType === OrganisationTypeConstants.ASSET_MANAGER && currentAssetManagerUuid) {
    return {
      filterBy: "assetManager",
      filterValue: currentAssetManagerUuid,
    };
  }
  if (currentDeveloperUuid) {
    return {
      filterBy: "developer",
      filterValue: currentDeveloperUuid,
    };
  }
  if (currentDevelopmentManagerUuid) {
    return {
      filterBy: "developmentManager",
      filterValue: currentDevelopmentManagerUuid,
    };
  }
  throw new Error("Organisation not currently linked to a developer, development manager or asset manager");
};

export const useProjects = (): UseProjectsReturnData => {
  const {
    currentUserType,
    currentDeveloperUuid,
    currentDevelopmentManagerUuid,
    currentAssetManagerUuid,
    hasPermission,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [refreshTasks, setRefreshTasks] = useState(false);
  const [phaseChartData, setPhaseChartData] = useState<ChartData>({});
  const [isLoading, setIsLoading] = useState(true);
  const [upcomingMilestones, setUpcomingMilestones] = useState<UpcomingMilestonePropsWithKey[]>([]);
  const [isLoadingMilestones, setIsLoadingMilestones] = useState(true);
  const [hasMilestonesError, setHasMilestonesError] = useState(false);
  /** Identifies the newest milestone fetch, so a slower earlier one cannot overwrite its result. */
  const milestonesRequestId = useRef(0);
  const [overdueTasks, setOverdueTasks] = useState<OverdueTaskProps>();
  const [isOverdueTasksPanelOpen, setIsOverdueTasksPanelOpen] = useState(false);
  const [isUpcomingMilestonesPanelOpen, setIsUpcomingMilestonesPanelOpen] = useState(false);
  const [selectedPanelTask, setSelectedPanelTask] = useState<SearchWorkflowTasksResponse["results"][0]>();
  const [showWorkflowTaskPanel, setShowWorkflowTaskPanel] = useState(false);

  const handleCloseWorkflowTaskPanel = useCallback((): void => {
    setSelectedPanelTask(undefined);
    setShowWorkflowTaskPanel(false);
  }, []);

  const workflow = useTaskPanelWorkflow(selectedPanelTask?.workflowUuid);

  const handleRefresh = useCallback((): void => {
    setRefreshTasks((prev) => !prev);
  }, []);

  const isAssetManager = currentUserType === OrganisationTypeConstants.ASSET_MANAGER;

  const tabsList = [
    {
      label: isAssetManager ? "Tracker" : "Project tracker",
      route: getProjectTrackerRoute(currentUserType),
    },
    {
      label: isAssetManager ? "Roadmap" : "Project roadmap",
      route: getProjectRoadmapRoute(currentUserType),
    },
  ];

  const canCreateProject = hasPermission(ResourcePermissionConstants.CREATE_RESOURCE);

  const onNewProjectClick = (): void => {
    navigate(getNewProjectRoute(currentUserType));
  };

  /** Counts by `cachedPhase`, keyed by phase label in phase order - see getProjectPhaseCounts. */
  const fetchPhaseCounts = useCallback(async (): Promise<ChartData | undefined> => {
    const organisationFilter = getProjectAggregateOrganisationFilter(
      currentUserType,
      currentDeveloperUuid,
      currentDevelopmentManagerUuid,
      currentAssetManagerUuid
    );
    const phaseAggregateResponse = await getProjectAggregate({
      ...organisationFilter,
      filterOperator: "eq",
      groupBy: "cachedPhase",
      aggregation: "count",
      aggregate: "id",
    });

    if (phaseAggregateResponse.status !== Status.Success || !phaseAggregateResponse.data) return undefined;

    return getProjectPhaseCounts(phaseAggregateResponse.data);
  }, [currentUserType, currentDeveloperUuid, currentDevelopmentManagerUuid, currentAssetManagerUuid]);

  const fetchData = useCallback(async () => {
    const phaseCounts = await fetchPhaseCounts();

    if (phaseCounts) {
      setPhaseChartData(phaseCounts);
    }
  }, [fetchPhaseCounts]);

  useEffect(() => {
    fetchData()
      .catch((error) => logError({ error }))
      .finally(() => {
        setIsLoading(false);
      });
  }, [fetchData]);

  const getAndSetOverdueTasks = useCallback(async (): Promise<void> => {
    const tasks = await getAggregateWorkflowTasks({
      activityUuid: null,
      phase: null,
      projectUuid: null,
      workflowUuid: null,
      dealUuid: null,
    });

    setOverdueTasks({
      amount: tasks.data?.overdueTasksCount ? tasks.data?.overdueTasksCount.toString() : "0",
      label: "Overdue tasks",
    });
  }, [refreshTasks]);

  const getAndSetUpcomingMilestones = useCallback(async (): Promise<void> => {
    // Completing a task toggles `refreshTasks` mid-flight, so two fetches can overlap. Only the
    // newest may touch milestone state: an older one landing last would otherwise overwrite fresh
    // cards, or flag an error over results that loaded fine.
    milestonesRequestId.current += 1;
    const requestId = milestonesRequestId.current;
    const isCurrentRequest = (): boolean => requestId === milestonesRequestId.current;

    setIsLoadingMilestones(true);
    setHasMilestonesError(false);
    try {
      const result = await searchWorkflowTasks({
        paging: {
          limit: 3,
          afterCursor: null,
          beforeCursor: null,
        },
        filter: {
          results: {
            isMilestone: {
              operator: "eq",
              value: true,
            },
            deadlineDate: {
              operator: "neq",
              value: null,
            },
            status: {
              operator: "neq",
              value: TaskConstants.TASK_STATUS_COMPLETE,
            },
          },
        },
        sort: [{ key: "deadlineDate" as "results.deadlineDate", direction: "asc" }],
      });
      if (!isCurrentRequest()) {
        return;
      }
      if (result.status !== Status.Success || result.data === undefined) {
        setHasMilestonesError(true);
        return;
      }
      setUpcomingMilestones(
        result.data.results.map((task) => ({
          key: task.uuid,
          name: task.name,
          deadlineDate: task.deadlineDate ? task.deadlineDate : undefined,
          status: task.status,
          assigneeName: task.assignee?.name ?? undefined,
          // The uniform parent, so the name and its link always refer to the same object — and
          // general milestone tasks, which have no `workflowObject*`, get one too.
          project: task.parentObjectName ?? undefined,
          onProjectClick: getTaskParentObjectNavigation(
            task.parentObjectType,
            task.parentObjectUuid,
            currentUserType,
            navigate,
            { goBackText: "Back to projects", from: location.pathname }
          ),
          onClick: () => {
            setSelectedPanelTask(task);
            setShowWorkflowTaskPanel(true);
          },
        }))
      );
    } catch {
      // A rejected search never reached the status check, which used to leave the card on its
      // skeletons for good.
      if (isCurrentRequest()) {
        setHasMilestonesError(true);
      }
    } finally {
      if (isCurrentRequest()) {
        setIsLoadingMilestones(false);
      }
    }
  }, [refreshTasks, currentUserType, navigate, location.pathname]);

  useEffect(() => {
    getAndSetOverdueTasks();
    getAndSetUpcomingMilestones();
  }, [refreshTasks]);

  return {
    tabsList,
    kpiCorners: isAssetManager ? "rounded" : "default",
    canCreateProject,
    onNewProjectClick,
    phaseChartData,
    isLoading,
    currentUserType,
    upcomingMilestones,
    overdueTasks: overdueTasks || { amount: "0", label: "Overdue tasks" },
    isOverdueTasksPanelOpen,
    setIsOverdueTasksPanelOpen,
    isLoadingMilestones,
    hasMilestonesError,
    isUpcomingMilestonesPanelOpen,
    setIsUpcomingMilestonesPanelOpen,
    workflow,
    selectedPanelTask,
    showWorkflowTaskPanel,
    handleCloseWorkflowTaskPanel,
    handleRefresh,
    refreshTasks,
    setSelectedPanelTask,
    setShowWorkflowTaskPanel,
  };
};
